/* eslint-disable react/display-name */
import React, { useEffect, useMemo, useState } from 'react'
import { differenceInMinutes, format } from 'date-fns'
import Loading from '../_library/Loading'
import MUIDataTable from 'mui-datatables'
import { pick, pickBy } from 'lodash'
import { AIRCRAFT_COLLECTION, TASK_COLLECTION, TEST_COLLECTION, USER_COLLECTION } from '../../_constants/globals'
import { useNavigate } from 'react-router-dom'
import { TESTS } from '../../_constants/routes'
import { useFirestore } from '../../hooks/useFirestore'
import { Chip, IconButton, Tooltip } from '@mui/material'
import PropTypes from 'prop-types'
import { Add } from '@mui/icons-material'
import TestRequestDialog from './TestRequestDialog'
import CircularProgressWithLabel from '../_library/CircularProgressWithLabel'
import { useSelector } from 'react-redux'
import { getTypeLabel, formatDistance } from '../../_helpers/dataHelper'


const STORAGE_KEY = 'allTestsTableState'
const TestTable = ({ aircraftId }) => {

  const testHooks = useFirestore(TEST_COLLECTION)
  const tests = testHooks.getDocs()
  const aircraftHooks = useFirestore(AIRCRAFT_COLLECTION)
  const taskHooks = useFirestore(TASK_COLLECTION)
  const dbTasks = taskHooks.getDocs()
  const userHooks = useFirestore(USER_COLLECTION)
  const dbUsers = userHooks.getDocs()
  const navigate = useNavigate()
  const readOnlyMode = useSelector(state => state.data.readOnlyMode)

  const [openNewTest, setOpenNewTest] = useState(false)

  useEffect(() => {
    testHooks.listen(pickBy({
      where: aircraftId && [['aircraftRef', '==', aircraftHooks.getDocRef(aircraftId)]],
      orderBy: [
        ['startDate', 'asc'],
        ['label', 'asc'],
      ],
    }))
    taskHooks.listen(pickBy({
      where: aircraftId && [['aircraftRef', '==', aircraftHooks.getDocRef(aircraftId)]],
      orderBy: [
        ['order', 'asc'],
      ],
    }))
    return () => testHooks.unsubscribe()
  }, [aircraftId])

  const data = useMemo(() => tests?.map(val => [
    val,
    val.startDate,
    getTypeLabel(val.aircraft?.type, true),
    val.aircraft?.serial,
    val.aircraft?.registration,
    val.testType,
    val.label.split('-').pop(),
    val.title,
    val.classification,
    val.purpose,
    val.users,
    dbTasks?.some(t => t.testRef.id === val.id && t.slug === 'captain-preflight' && t.completedTime) ? '✔' : '',
    dbTasks?.filter(t => t.testRef.id === val.id),
    val.departure,
    val.arrival,
    val.offBlocksTime,
    val.takeOffTime,
    val.landingTime,
    val.onBlocksTime,
    val.onBlocksTime && val.offBlocksTime ? formatDistance(differenceInMinutes(val.onBlocksTime, val.offBlocksTime)) : '',
    val.landingTime && val.takeOffTime ? formatDistance(differenceInMinutes(val.landingTime, val.takeOffTime)) : '',
    val.cerHours && formatDistance(val.cerHours * 60),
    dbTasks?.some(t => t.testRef.id === val.id && t.slug === 'captain-final-check' && t.completedTime) ? '✔' : '',
    val.comments || '',
  ]), [tests, dbTasks])

  const localState = useMemo(() => {
    const state = JSON.parse(window.localStorage.getItem(STORAGE_KEY))
    return state?.columns.length === 24 ? state : undefined
  }, [])

  if (!tests) return <Loading />
  return (<>
    <MUIDataTable
      data={data}
      columns={[
        { name: 'object', options: { filter: false, sort: false, display: 'excluded', print: false, searchable: false, download: false } },
        {
          name: 'date',
          label: 'Date',
          options: {
            display: localState?.columns[1].display || 'true',
            filter: true,
            filterList: localState?.filterList[1] || [],
            sortThirdClickReset: true,
            sortDescFirst: true,
            customBodyRender: val => val && format(val, 'dd-MM-yyyy'),
          },
        },
        {
          name: 'aircraftType',
          label: 'Aircraft type',
          options: {
            display: localState?.columns[2].display || 'false',
            filter: true,
            filterType: 'multiselect',
            filterList: localState?.filterList[2] || [],
            sortThirdClickReset: true,
          },
        },
        {
          name: 'aircraftSerial',
          label: 'MSN',
          options: {
            display: localState?.columns[3].display || aircraftId ? 'false' : 'true',
            filter: true,
            filterType: 'multiselect',
            filterList: localState?.filterList[3] || [],
            sortThirdClickReset: true,
          },
        },
        {
          name: 'aircraftImmat',
          label: 'Immat',
          options: {
            display: localState?.columns[4].display || 'false',
            filter: true,
            filterType: 'multiselect',
            filterList: localState?.filterList[4] || [],
            sortThirdClickReset: true,
          },
        },
        {
          name: 'testType',
          label: 'Test type',
          options: {
            display: localState?.columns[5].display || 'false',
            filter: true,
            filterType: 'multiselect',
            filterList: localState?.filterList[5] || [],
            sortThirdClickReset: true,
          },
        },
        {
          name: 'ref',
          label: 'Ref TO',
          options: {
            display: localState?.columns[6].display || 'true',
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          name: 'title',
          label: 'Title',
          options: {
            display: localState?.columns[7].display || 'false',
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          name: 'classification',
          label: 'Class',
          options: {
            display: localState?.columns[8].display || 'true',
            filter: true,
            filterList: localState?.filterList[8] || [],
            sortThirdClickReset: true,
          },
        },
        {
          name: 'purpose',
          label: 'Purpose',
          options: {
            display: localState?.columns[9].display || 'false',
            filter: true,
            filterType: 'multiselect',
            filterList: localState?.filterList[9] || [],
            sortThirdClickReset: true,
          },
        },
        {
          name: 'users',
          label: 'Crew',
          options: {
            display: localState?.columns[10].display || 'true',
            filter: true,
            filterList: localState?.filterList[10] || [],
            sortThirdClickReset: true,
            customBodyRender: val => val?.map(u => <Chip key={u.email} label={u.trigram} />),
            sort: false,
            filterType: 'multiselect',
            filterOptions: {
              names: dbUsers.map(u => u.trigram).filter(t => !!t),
              logic: (users, filters) => !users?.some(u => filters.includes(u.trigram)),
            },
          },
        },
        {
          name: 'captainPreflight',
          label: 'Visa CdB prévol',
          options: {
            display: localState?.columns[11].display || 'true',
            filter: true,
            filterList: localState?.filterList[11] || [],
            sortThirdClickReset: true,
          },
        },
        {
          name: 'tasks',
          label: 'Tasks',
          options: {
            display: localState?.columns[12].display || 'true',
            sortThirdClickReset: true,
            customBodyRender: tasks => tasks && <CircularProgressWithLabel completed={tasks.reduce((acc, val) => val.completedTime ? acc + 1 : acc, 0)} total={tasks.length} />,
          },
        },
        {
          name: 'departure',
          label: 'Departure',
          options: {
            display: localState?.columns[13].display || 'false',
            filter: true,
            filterList: localState?.filterList[13] || [],
            sortCompare: order => (a, b) => a.data.ICAO.localeCompare(b.data.ICAO) * (order === 'asc' ? 1 : -1),
            sortThirdClickReset: true,
            customBodyRender: val => typeof val === 'object' ? val.ICAO : val,
          },
        },
        {
          name: 'arrival',
          label: 'Arrival',
          options: {
            display: localState?.columns[14].display || 'false',
            filter: true,
            filterList: localState?.filterList[14] || [],
            sortCompare: order => (a, b) => a.data.ICAO.localeCompare(b.data.ICAO) * (order === 'asc' ? 1 : -1),
            sortThirdClickReset: true,
            customBodyRender: val => typeof val === 'object' ? val.ICAO : val,
          },
        },
        {
          name: 'offBlocksTime',
          label: 'Off blocks',
          options: {
            display: localState?.columns[15].display || 'false',
            filter: false,
            sortThirdClickReset: true,
            customBodyRender: val => val && format(val, 'HH:mm'),
          },
        },
        {
          name: 'takeoffTime',
          label: 'Takeoff',
          options: {
            display: localState?.columns[16].display || 'false',
            filter: false,
            sortThirdClickReset: true,
            customBodyRender: val => val && format(val, 'HH:mm'),
          },
        },
        {
          name: 'landingTime',
          label: 'Landing',
          options: {
            display: localState?.columns[17].display || 'false',
            filter: false,
            sortThirdClickReset: true,
            customBodyRender: val => val && format(val, 'HH:mm'),
          },
        },
        {
          name: 'onBlocksTime',
          label: 'On blocks',
          options: {
            display: localState?.columns[18].display || 'false',
            filter: false,
            sortThirdClickReset: true,
            customBodyRender: val => val && format(val, 'HH:mm'),
          },
        },
        {
          name: 'blockTime',
          label: 'Block time',
          options: {
            display: localState?.columns[19].display || 'false',
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          name: 'flightTime',
          label: 'Flight time',
          options: {
            display: localState?.columns[20].display || 'true',
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          name: 'cerTime',
          label: 'CER time',
          options: {
            display: localState?.columns[21].display || 'true',
            filter: false,
            sortThirdClickReset: true,
          },
        },
        {
          name: 'captainFinalCheck',
          label: 'Captain final check',
          options: {
            display: localState?.columns[22].display || 'true',
            filter: true,
            filterList: localState?.filterList[22] || [],
            sortThirdClickReset: true,
          },
        },
        {
          name: 'comments',
          label: 'Comments',
          options: {
            display: localState?.columns[23].display || 'false',
            filter: false,
            sortThirdClickReset: true,
          },
        },
      ]}
      options={{
        selectableRows: 'none',
        downloadOptions: {
          filename: 'tests.csv',
          separator: ';',
          filterOptions: {
            useDisplayedColumnsOnly: false,
            useDisplayedRowsOnly: true,
          },
        },
        setRowProps: () => ({
          sx: {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        }),
        onRowClick: (row, { dataIndex }) => navigate(TESTS + '/' + tests[dataIndex].id),
        onTableChange: (action, tableState) => {
          if (action === 'propsUpdate') return
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pick(tableState, ['columns', 'filterList', 'sortOrder', 'rowsPerPage'])))
        },
        sortOrder: localState?.sortOrder || {},
        rowsPerPage: localState?.rowsPerPage || 10,
        jumpToPage: true,
        rowsPerPageOptions: [10, 50, 100],
        pagination: !aircraftId,
        customToolbar: () => (<>
          <Tooltip title='Nouveau vol'>
            <span>
              <IconButton color='primary' onClick={() => setOpenNewTest(true)} disabled={readOnlyMode}>
                <Add />
              </IconButton>
            </span>
          </Tooltip>
        </>),
      }}
    />
    {openNewTest && <TestRequestDialog open onClose={() => setOpenNewTest(false)} aircraftInitId={aircraftId} />}
  </>)
}

TestTable.propTypes = {
  aircraftId: PropTypes.string,
}

export default TestTable
